import React from 'react';
import { useDrop } from "react-dnd";
import { Grid, Button, useTheme } from "@mui/material";
import { useCookies } from "react-cookie";
import { Cohort } from "../../interfaces/Clinic";
import ClinicService from "../../services/ClinicService";

interface CohortTabBarProps {
    cohortList: Cohort[];
    activateCohort: (cohort: Cohort) => void;
    activeCohort?: Cohort | null;
    refreshPatients: () => void;
}

export const CohortTabBar = ({
    cohortList,
    activateCohort,
    activeCohort,
    refreshPatients,
}: CohortTabBarProps) => {
    const theme = useTheme();
    
    console.log("CohortTabBar render - activeCohort:", activeCohort, "cohortList:", cohortList);

    return (
        <>
            {/* Cohort list */}
            <Grid container direction="row" sx={{ paddingBottom: 1 }}>
                {cohortList?.map((cohort: Cohort, index: number) => (
                    <CohortTab
                        key={cohort.id || cohort.name || index}
                        cohort={cohort}
                        activateCohort={activateCohort}
                        active={String((activeCohort as any)?.cohort_id || activeCohort?.id || '') === String((cohort as any).cohort_id || cohort.id || '')}
                        activeCohort={activeCohort}
                        refreshPatients={refreshPatients}
                        theme={theme}
                    />
                ))}
            </Grid>
        </>
    );
};

interface CohortTabProps {
    cohort: Cohort;
    activateCohort: (cohort: Cohort) => void;
    active: boolean;
    activeCohort?: Cohort | null;
    refreshPatients: () => void;
    theme: any;
}

export const CohortTab = ({ 
    cohort, 
    activateCohort, 
    active, 
    activeCohort, 
    refreshPatients, 
    theme 
}: CohortTabProps) => {
    const [cookies, ,] = useCookies(["access_token"]);
    
    // Debug the active state calculation
    console.log("CohortTab debug:", {
        cohortName: cohort.name,
        cohortObj: cohort,
        activeCohortObj: activeCohort,
        active: active,
        cohortKeys: Object.keys(cohort),
        activeCohortKeys: activeCohort ? Object.keys(activeCohort) : null
    });
    
    const selectCohort = (c: Cohort) => {
        activateCohort(c);
    };

    const [{ isOver }, drop] = useDrop({
        accept: 'PATIENT_ITEM',
        drop: (item: any) => {
            // Handle patient assignment to cohort
            if (!item.id || !cohort.id) {
                console.error("Invalid patient or cohort ID");
                return;
            }

            // Use the new patient association endpoint
            const associationData = {
                cohort: cohort.id,
                patient_id: item.id,
                notes: `Assigned via drag-and-drop on ${new Date().toISOString()}`,
                is_active: true
            };

            ClinicService.associatePatient(associationData, cookies.access_token)
                .then((res: any) => {
                    console.log("Patient successfully assigned to cohort:", res);
                    refreshPatients();
                })
                .catch((err: any) => {
                    console.error("Error assigning patient to cohort:", err);
                });
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    return (
        <div ref={drop} style={{ 
            backgroundColor: isOver ? theme.palette.action.hover : theme.palette.background.paper 
        }}>
            <Button
                variant={active ? "contained" : "outlined"}
                sx={{
                    marginRight: 1,
                }}
                onClick={() => selectCohort(cohort)}
            >
                {cohort.name}
            </Button>
        </div>
    );
};

// Legacy compatibility component that wraps CohortTabBar for VICU interface
interface LegacyVicuTabBarProps {
    vicuList: any[];
    activateVicu: any;
    activeVicu?: any;
    refreshDevices: any;
}

export const VicuTabBar = ({
    vicuList,
    activateVicu,
    activeVicu,
    refreshDevices,
}: LegacyVicuTabBarProps) => {
    const theme = useTheme();

    return (
        <>
            {/* Legacy VICU list - maps to cohorts */}
            <Grid container direction="row" sx={{ paddingBottom: 1 }}>
                {vicuList?.map((vicu: any) => (
                    <LegacyVicuTab
                        key={vicu.group_id || vicu.id}
                        vicu={vicu}
                        activateVicu={activateVicu}
                        active={activeVicu === vicu}
                        activeGroup={activeVicu}
                        refreshDevices={refreshDevices}
                        theme={theme}
                    />
                ))}
            </Grid>
        </>
    );
};

interface LegacyVicuTabProps {
    vicu: any;
    activateVicu: any;
    active: boolean;
    activeGroup?: any;
    refreshDevices: any;
    theme: any;
}

const LegacyVicuTab = ({ vicu, activateVicu, active, activeGroup, refreshDevices, theme }: LegacyVicuTabProps) => {
    const [cookies, ,] = useCookies(["access_token"]);
    const selectVICU = (v: any) => {
        activateVicu(v);
    };

    const [{ isOver }, drop] = useDrop({
        accept: 'ITEM',
        drop: (item: any) => {
            // Legacy device management - for backward compatibility
            if (vicu.group_name === "syspool") {
                ClinicService.manageGroupDevices(
                    activeGroup.group_id,
                    { type: "detach", devices: [{ "device_id": item.id }] },
                    cookies.access_token
                )
                    .then((res: any) => {
                        refreshDevices();
                    })
                    .catch((err: any) => {
                        console.log(err);
                    });
                return;
            }

            ClinicService.manageGroupDevices(
                vicu.group_id,
                { type: "attach", devices: [{ "device_id": item.id }] },
                cookies.access_token
            )
                .then((res: any) => {
                    refreshDevices();
                })
                .catch((err: any) => {
                    console.log(err);
                });
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    return (
        <div ref={drop} style={{ 
            backgroundColor: isOver ? theme.palette.action.hover : theme.palette.background.paper 
        }}>
            <Button
                variant={active ? "contained" : "outlined"}
                sx={{
                    marginRight: 1,
                    color: active
                        ? theme.palette.mode === 'dark'
                            ? theme.palette.text.primary
                            : theme.palette.common.white
                        : vicu.vicu_type === "pool"
                            ? "darkorange"
                            : theme.palette.text.primary,
                    backgroundColor: active ? (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.main) : undefined,
                    borderColor: theme.palette.divider,
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    }
                }}
                onClick={() => selectVICU(vicu)}
            >
                {vicu.group_name || vicu.name}
            </Button>
        </div>
    );
};